import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMeetingState } from '../contexts/LiveShareContext.js';
import { usePlatform } from '../contexts/PlatformContext.js';
import {
  type PresentingSurface,
  forgetPresenting,
  recordResumeArmed,
  recordResumeShown,
  rememberPresenting,
  sessionMemory,
  shouldOfferResume,
} from '../utils/presenting-memory.js';

export interface MeetingControlsProps {
  /**
   * Which surface this strip is on. Both can present, and both share
   * sessionStorage, so the resume offer has to tell them apart -- see
   * presenting-memory.
   */
  surface: PresentingSurface;
}

/**
 * Meeting control strip for presenter mode and follow-me indicators.
 * Used in both SidePanel and MeetingStage.
 */
export function MeetingControls({ surface }: MeetingControlsProps) {
  const {
    isReady,
    isPresenting,
    isFollowing,
    isSuspended,
    startPresenting,
    stopPresenting,
    beginSuspension,
    endSuspension,
  } = useMeetingState();
  const { context } = usePlatform();
  const meetingId = context?.meetingId ?? null;

  const storage = useMemo(() => sessionMemory(), []);
  const [offerResume, setOfferResume] = useState(false);
  const wasPresentingRef = useRef(false);

  // Read the flag once this client knows which meeting it is in. A rebuilt
  // panel arrives here with isPresenting false and nothing in memory, so
  // storage is the only thing that can still say it was presenting.
  useEffect(() => {
    const offer = shouldOfferResume(storage, surface, meetingId);
    setOfferResume(offer);
    if (offer) recordResumeArmed();
  }, [storage, surface, meetingId]);

  // Track presenting across the page's life. Seeing the true -> false
  // transition means the user pressed Stop, or someone else took over: a
  // rebuild never gets that far, because the page is gone. That asymmetry is
  // the whole discriminator -- see presenting-memory.
  useEffect(() => {
    if (isPresenting) {
      wasPresentingRef.current = true;
      rememberPresenting(storage, surface, meetingId);
      setOfferResume(false);
    } else if (wasPresentingRef.current) {
      wasPresentingRef.current = false;
      forgetPresenting(storage, surface);
      setOfferResume(false);
    }
  }, [isPresenting, storage, surface, meetingId]);

  // The strip's own render condition, lifted out so it can be recorded. The
  // window where it holds can be very short -- when Live Share hands the
  // presenter role back, "Presenting" replaces it almost at once -- so
  // watching the screen is not a reliable way to know whether it appeared.
  const resumeOfferVisible =
    isReady && offerResume && !isPresenting && !isFollowing && !isSuspended;

  useEffect(() => {
    if (resumeOfferVisible) recordResumeShown();
  }, [resumeOfferVisible]);

  const dismissResume = useCallback(() => {
    forgetPresenting(storage, surface);
    setOfferResume(false);
  }, [storage, surface]);

  if (!isReady) return null;

  return (
    <div className="meeting-controls">
      {/* Presenting controls */}
      {isPresenting ? (
        <div className="meeting-controls__status meeting-controls__status--presenting">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
          </svg>
          <span>Presenting</span>
          {/* Stop ends the follow relationship and nothing else: it sends no
              instruction to close anything, and no follower reacts to
              following ending, so a follower keeps the document they are
              looking at. Confirmed on a phone and decided to keep on
              2026-09-02 -- someone may still be reading or annotating, and
              taking the page away mid-sentence is the hostility "never
              hard-block mid-meeting" rules out elsewhere. Closing it for
              followers would need a broadcast the presenter never sends; do
              not add one here without revisiting that decision. */}
          <button
            className="meeting-controls__btn meeting-controls__btn--stop"
            onClick={() => void stopPresenting()}
          >
            Stop
          </button>
        </div>
      ) : isFollowing ? (
        <div className="meeting-controls__status meeting-controls__status--following">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>Following presenter</span>
          <button
            className="meeting-controls__btn meeting-controls__btn--unfollow"
            onClick={() => void beginSuspension()}
          >
            Navigate freely
          </button>
        </div>
      ) : isSuspended ? (
        <div className="meeting-controls__status meeting-controls__status--suspended">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
          <span>You left the presenter's view</span>
          <button
            className="meeting-controls__btn meeting-controls__btn--rejoin"
            onClick={() => void endSuspension()}
          >
            Rejoin
          </button>
        </div>
      ) : resumeOfferVisible ? (
        /* This client was presenting and the panel was rebuilt under it --
           Live Share dropped the presenter role and nothing restored it, so
           followers are stranded on their last page while the presenter sees
           no sign anything changed (2026-09-18). Deliberately an offer, not an
           automatic re-assertion: someone may have taken over legitimately
           while this panel was down. */
        <div className="meeting-controls__status meeting-controls__status--lapsed">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>You&rsquo;ve stopped presenting</span>
          <button
            className="meeting-controls__btn meeting-controls__btn--resume"
            onClick={() => void startPresenting()}
          >
            Resume
          </button>
          <button
            className="meeting-controls__btn meeting-controls__btn--dismiss"
            onClick={dismissResume}
            title="Dismiss"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="meeting-controls__status meeting-controls__status--idle">
          <button
            className="meeting-controls__btn meeting-controls__btn--present"
            onClick={() => void startPresenting()}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
            </svg>
            Present
          </button>
        </div>
      )}
    </div>
  );
}
